// src/pages/FaqPage.jsx
import React, { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle, Search, Users, Book, Settings, MessageSquare, CreditCard } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useTranslation } from 'react-i18next';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';

const FaqPage = () => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const [openItem, setOpenItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Scroll animations following the same pattern as HomePage/AboutUs
  const [heroRef, heroIsVisible] = useScrollAnimation();
  const [faqRef, faqIsVisible] = useScrollAnimation();
  const [ctaRef, ctaIsVisible] = useScrollAnimation();

  // Function to navigate to contact page
  const handleContactClick = () => {
    navigate('/contact');
  };

  // Function to convert markdown-style formatting to HTML
  const formatText = (text) => {
    if (!text) return '';

    return text
      // Convert **bold** to <strong>bold</strong>
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Convert \n to actual line breaks
      .replace(/\\n/g, '<br>')
      // Convert single \n to line breaks as well
      .replace(/\n/g, '<br>');
  };

  // FAQ data with translation keys and categories
  const faqData = [
    {
      question: t('faq.questions.appPurpose'),
      answer: formatText(t('faq.answers.appPurpose')),
      category: 'general',
      icon: Book
    },
    {
      question: t('faq.questions.examTypes'),
      answer: formatText(t('faq.answers.examTypes')),
      category: 'exams',
      icon: Book
    },
    {
      question: t('faq.questions.languages'),
      answer: formatText(t('faq.answers.languages')),
      category: 'general',
      icon: Users
    },
    {
      question: t('faq.questions.features'),
      answer: formatText(t('faq.answers.features')),
      category: 'features',
      icon: Settings
    },
    {
      question: t('faq.questions.studyPlan'),
      answer: formatText(t('faq.answers.studyPlan')),
      category: 'features',
      icon: Settings
    },
    {
      question: t('faq.questions.examSections'),
      answer: formatText(t('faq.answers.examSections')),
      category: 'exams',
      icon: Book
    },
    {
      question: t('faq.questions.download'),
      answer: formatText(t('faq.answers.download')),
      category: 'technical',
      icon: MessageSquare
    },
    {
      question: t('faq.questions.devices'),
      answer: formatText(t('faq.answers.devices')),
      category: 'technical',
      icon: MessageSquare
    },
    {
      question: t('faq.questions.account'),
      answer: formatText(t('faq.answers.account')),
      category: 'general',
      icon: Users
    },
    {
      question: t('faq.questions.progress'),
      answer: formatText(t('faq.answers.progress')),
      category: 'features',
      icon: Settings
    },
    // Exam knowledge (SEO: comparison, mock fidelity, prep time, scoring, retakes)
    {
      question: t('faq.questions.telcVsGoethe'),
      answer: formatText(t('faq.answers.telcVsGoethe')),
      category: 'exams',
      icon: Book
    },
    {
      question: t('faq.questions.mockSimilarity'),
      answer: formatText(t('faq.answers.mockSimilarity')),
      category: 'exams',
      icon: Book
    },
    {
      question: t('faq.questions.mocksEnough'),
      answer: formatText(t('faq.answers.mocksEnough')),
      category: 'exams',
      icon: Book
    },
    {
      question: t('faq.questions.prepTime'),
      answer: formatText(t('faq.answers.prepTime')),
      category: 'exams',
      icon: Book
    },
    {
      question: t('faq.questions.passScore'),
      answer: formatText(t('faq.answers.passScore')),
      category: 'exams',
      icon: Book
    },
    {
      question: t('faq.questions.failedModule'),
      answer: formatText(t('faq.answers.failedModule')),
      category: 'exams',
      icon: Book
    },
    {
      question: t('faq.questions.schreibenScoring'),
      answer: formatText(t('faq.answers.schreibenScoring')),
      category: 'exams',
      icon: Book
    },
    {
      question: t('faq.questions.selfStudy'),
      answer: formatText(t('faq.answers.selfStudy')),
      category: 'exams',
      icon: Book
    },
    {
      question: t('faq.questions.freeMock'),
      answer: formatText(t('faq.answers.freeMock')),
      category: 'exams',
      icon: Book
    },
    {
      question: t('faq.questions.examForGoal'),
      answer: formatText(t('faq.answers.examForGoal')),
      category: 'exams',
      icon: Book
    },
    // Pricing & Payment
    {
      question: t('faq.questions.paymentOneTime'),
      answer: formatText(t('faq.answers.paymentOneTime')),
      category: 'pricing',
      icon: CreditCard
    },
    {
      question: t('faq.questions.paymentAccess'),
      answer: formatText(t('faq.answers.paymentAccessV2')),
      category: 'pricing',
      icon: CreditCard
    },
    {
      question: t('faq.questions.paymentFree'),
      answer: formatText(t('faq.answers.paymentFree')),
      category: 'pricing',
      icon: CreditCard
    },
    {
      question: t('faq.questions.paymentMethods'),
      answer: formatText(t('faq.answers.paymentMethods')),
      category: 'pricing',
      icon: CreditCard
    },
    {
      question: t('faq.questions.paymentRefund'),
      answer: formatText(t('faq.answers.paymentRefund')),
      category: 'pricing',
      icon: CreditCard
    },
  ];

  // FAQPage JSON-LD structured data, enables expandable answers in Google search results
  useEffect(() => {
    const faqLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqData.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          // Strip HTML tags from the formatted answer for clean plain text in structured data
          text: item.answer.replace(/<[^>]*>/g, ''),
        },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(faqLd);
    script.setAttribute('data-seo-faq', '');
    document.head.appendChild(script);
    return () => {
      const existing = document.querySelector('script[data-seo-faq]');
      if (existing?.parentNode) existing.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SEO: meta title and description for FAQ page
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'FAQ | Logami – German Exam Prep Questions Answered';

    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content') || '';
    const created = !metaDesc;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Got questions about Logami? Find answers about TELC, Goethe and ÖSD exam prep, features, pricing, and study plans. Everything you need to know before you start.');

    return () => {
      document.title = prevTitle;
      if (created && metaDesc?.parentNode) metaDesc.remove();
      else if (metaDesc) metaDesc.setAttribute('content', prevDesc);
    };
  }, []);

  // Filter FAQ items based on search
  const filteredFaqData = faqData.filter(item =>
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    // Search in the raw text without HTML tags
    item.answer.replace(/<[^>]*>/g, '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Category colors - Black & White theme
  const categoryColors = {
    general: 'bg-black/5 text-black border-black/20',
    features: 'bg-black/5 text-black border-black/20',
    exams: 'bg-black/5 text-black border-black/20',
    technical: 'bg-black/5 text-black border-black/20',
    pricing: 'bg-[#3A86FF]/10 text-[#3A86FF] border-[#3A86FF]/20',
  };

  const FaqItem = ({ question, answer, isOpen, onClick, category, icon: IconComponent }) => {
  return (
    <div className="group mb-4">
      <div
        className={`
          relative bg-white border-2 rounded-2xl cursor-pointer overflow-hidden
          transition-all duration-500
          ${isOpen 
            ? 'border-black shadow-2xl scale-[1.02]' 
            : 'border-black/10 hover:border-black/30 hover:shadow-xl hover:scale-[1.01]'
          }
        `}
        onClick={onClick}
      >
        {/* Animated geometric background patterns */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Diagonal lines */}
          <div className={`
            absolute inset-0 transition-all duration-700
            ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
          `}>
            <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-black/5 to-transparent" />
            <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-black/5 to-transparent" />
          </div>

          {/* Radial gradient overlay on hover */}
          <div className={`
            absolute inset-0 transition-opacity duration-500
            ${isOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}
          `} style={{
            background: 'radial-gradient(circle at top left, rgba(0,0,0,0.02) 0%, transparent 50%)'
          }} />

          {/* Shine effect on hover */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        </div>

        {/* Animated background wave on open */}
        <div className={`
          absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50
          transition-all duration-500
          ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        `} />

        {/* Question Header */}
        <div className="relative z-10 p-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            {/* Icon with multiple animation layers */}
            <div className={`
              relative flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
              transition-all duration-500
              ${isOpen 
                ? 'bg-black text-white scale-110 rotate-6' 
                : 'bg-black/5 text-black group-hover:bg-black group-hover:text-white group-hover:scale-110'
              }
            `}>
              {/* Icon glow on open */}
              {isOpen && (
                <div className="absolute inset-0 bg-black rounded-xl blur-md opacity-30 animate-pulse" />
              )}
              <IconComponent className="w-6 h-6 relative z-10 transition-transform duration-300 group-hover:rotate-12" />

              {/* Corner accent dots */}
              {isOpen && (
                <>
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-white rounded-full animate-ping" />
                  <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 bg-white rounded-full animate-ping" style={{ animationDelay: '0.5s' }} />
                </>
              )}
            </div>

            {/* Question Text with underline animation */}
            <div className="relative flex-1">
              <h3 className={`
                font-space text-lg font-bold transition-all duration-300
                ${isOpen ? 'text-black' : 'text-black/80 group-hover:text-black'}
              `}>
                {question}
              </h3>
              {/* Animated underline on hover/open */}
              <div className={`
                absolute bottom-0 left-0 h-0.5 bg-black transition-all duration-500
                ${isOpen ? 'w-full' : 'w-0 group-hover:w-1/3'}
              `} />
            </div>
          </div>

          {/* Chevron with rotation and scale */}
          <div className="relative flex-shrink-0">
            <ChevronDown className={`
              w-6 h-6 transition-all duration-500
              ${isOpen 
                ? 'rotate-180 text-black scale-125' 
                : 'rotate-0 text-black/50 group-hover:text-black group-hover:scale-110'
              }
            `} />
            {/* Chevron circle background on open */}
            {isOpen && (
              <div className="absolute inset-0 -m-2 bg-black/5 rounded-full animate-ping" />
            )}
          </div>
        </div>

        {/* Answer Content with enhanced animation */}
        <div className={`
          relative z-10 overflow-hidden transition-all duration-500
          ${isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}
        `}>
          <div className="px-6 pb-6 pt-2">
            {/* Animated divider line */}
            <div className="relative h-px bg-black/10 mb-4 overflow-hidden">
              <div className={`
                absolute inset-0 bg-gradient-to-r from-transparent via-black to-transparent
                transition-transform duration-1000
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
              `} />
            </div>

            {/* Answer text with fade-in animation */}
            <div
              className={`
                text-black/70 leading-relaxed prose prose-sm max-w-none
                transition-all duration-700 delay-100
                ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
              `}
              dangerouslySetInnerHTML={{ __html: answer }}
            />
          </div>
        </div>

        {/* Enhanced decorative corner accents with animation */}
        {isOpen && (
          <>
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-black/20 rounded-tl-2xl animate-pulse" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-black/20 rounded-br-2xl animate-pulse" style={{ animationDelay: '0.5s' }} />

            {/* Small decorative dots in corners */}
            <div className="absolute top-2 left-2 w-2 h-2 bg-black/20 rounded-full" />
            <div className="absolute bottom-2 right-2 w-2 h-2 bg-black/20 rounded-full" />
          </>
        )}

        {/* Border pulse effect on hover */}
        <div className={`
          absolute inset-0 rounded-2xl pointer-events-none
          transition-opacity duration-300
          ${isOpen ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}
        `}>
          <div className="absolute inset-0 rounded-2xl border-2 border-black animate-ping" style={{ animationDuration: '2s' }} />
        </div>
      </div>
    </div>
  );
};

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Enhanced with animations */}
      <section ref={heroRef} className={`
        relative py-16 sm:py-24 bg-white overflow-hidden
        transition-all duration-700
        ${heroIsVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}
      `}>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-block px-5 py-2 bg-black text-white text-xs font-bold tracking-widest uppercase rounded-full mb-6 animate-pulse">
              ❓ {t('faq.badge', 'Help Center')}
            </div>

            {/* Title */}
            <h1 className="font-space text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-black mb-6 leading-tight">
              {t('faq.title')}
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-black/70 leading-relaxed max-w-3xl mx-auto mb-10">
              {t('faq.subtitle')}
            </p>

            {/* Search Bar - Enhanced */}
            <div className="relative max-w-xl mx-auto group">
              <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none transition-all duration-300">
                <Search className="h-5 w-5 text-black/40 group-focus-within:text-black" />
              </div>
              <input
                type="text"
                placeholder={t('faq.searchPlaceholder', 'Search questions...')}
                className="w-full pl-14 pr-6 py-4 bg-white border-2 border-black/10 rounded-xl shadow-sm focus:ring-2 focus:ring-black focus:border-black transition-all duration-300 font-medium text-black placeholder-black/40"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {/* Search underline animation */}
              <div className="absolute bottom-0 left-0 h-1 bg-black w-0 group-focus-within:w-full transition-all duration-500" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content Section - Enhanced */}
      <section ref={faqRef} className={`
  relative py-16 bg-gradient-to-br from-gray-50 via-white to-gray-100
  transition-all duration-700 delay-200 overflow-hidden
  ${faqIsVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}
`}>
  {/* Complex geometric background */}
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Diagonal lines */}
    <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-black/10 to-transparent" />
    <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-black/10 to-transparent" />
    <div className="absolute top-0 left-1/2 w-px h-full bg-gradient-to-b from-transparent via-black/5 to-transparent" />

    {/* Radial gradients for depth */}
    <div
      className="absolute w-96 h-96 rounded-full opacity-[0.03]"
      style={{
        top: '20%',
        left: '10%',
        background: 'radial-gradient(circle, black 0%, transparent 70%)'
      }}
    />
    <div
      className="absolute w-80 h-80 rounded-full opacity-[0.03]"
      style={{
        bottom: '20%',
        right: '15%',
        background: 'radial-gradient(circle, black 0%, transparent 70%)'
      }}
    />
  </div>

  <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
    {/* FAQ Items */}
<div className="space-y-3">
  {filteredFaqData.length > 0 ? (
    filteredFaqData.map((item, index) => {
      // Calculate if this item should be visible
      const shouldAnimate = faqIsVisible;
      const delay = index * 100; // 100ms between each item

      return (
        <div
          key={index}
          className="opacity-0"
          style={{
            animation: shouldAnimate ? `faqPopIn 0.6s ease-out ${delay}ms forwards` : 'none'
          }}
        >
          <FaqItem
            question={item.question}
            answer={item.answer}
            category={item.category}
            icon={item.icon}
            isOpen={openItem === index}
            onClick={() => setOpenItem(openItem === index ? null : index)}
          />
        </div>
      );
    })
  ) : (
    <div
      className="text-center py-16 bg-white rounded-2xl border-2 border-black/10 shadow-xl opacity-0"
      style={{
        animation: faqIsVisible ? 'faqPopIn 0.6s ease-out 200ms forwards' : 'none'
      }}
    >
      <div className="w-20 h-20 bg-black/5 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
        <Search className="w-10 h-10 text-black/40" />
      </div>
      <h3 className="font-space text-xl font-bold text-black mb-3">
        {t('faq.noResults', 'No questions found')}
      </h3>
      <p className="text-black/60">
        {t('faq.noResultsDesc', 'Try adjusting your search terms or browse all questions above.')}
      </p>
    </div>
  )}
</div>
  </div>
</section>

      {/* CTA Section - Enhanced with black & white theme */}
      <section ref={ctaRef} className={`
        py-16 sm:py-24 bg-white
        transition-all duration-700 delay-400
        ${ctaIsVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}
      `}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-white rounded-3xl p-10 sm:p-16 text-center border-[3px] border-black overflow-hidden group">
            <div className="relative z-10">
              {/* Icon */}
              <div className="w-20 h-20 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                <MessageSquare className="w-10 h-10 text-white" />
              </div>

              {/* Title */}
              <h2 className="font-space text-3xl sm:text-4xl font-black text-black mb-4 tracking-tight">
                {t('faq.cta.title', 'Still have questions?')}
              </h2>

              {/* Description */}
              <p className="text-lg text-black/70 mb-10 max-w-md mx-auto leading-relaxed">
                {t('faq.contactMessage')}
              </p>

              {/* CTA Button */}
              <button
                onClick={handleContactClick}
                className="group/btn relative px-10 py-4 bg-black text-white font-space font-black text-lg rounded-xl border-3 border-black overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-2xl cursor-pointer"
              >
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-black to-gray-800 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500" style={{
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 3s infinite'
                }} />

                {/* Button content */}
                <span className="relative z-10 flex items-center justify-center gap-3">
                  <MessageSquare className="w-5 h-5 transition-transform duration-300 group-hover/btn:scale-110" />
                  <span>{t('faq.cta.button', 'Contact Support')}</span>
                  <ChevronDown className="w-5 h-5 transition-all duration-300 group-hover/btn:translate-x-2 group-hover/btn:rotate-[-90deg]" />
                </span>
              </button>
            </div>

            {/* Decorative corners */}
            <div className="absolute top-0 left-0 w-20 h-20 border-t-4 border-l-4 border-black/10 rounded-tl-3xl" />
            <div className="absolute bottom-0 right-0 w-20 h-20 border-b-4 border-r-4 border-black/10 rounded-br-3xl" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default FaqPage;